import React, { useState } from 'react';
import { PRODUCTS, Product } from '../data/products';

interface ProductExplorerProps {
  onQuerySelect: (query: string) => void;
}

export const ProductExplorer: React.FC<ProductExplorerProps> = ({ onQuerySelect }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = ['all', '건강보험', '운전자보험', '종신보험', '종합보험'];

  const filteredProducts = selectedCategory === 'all'
    ? PRODUCTS
    : PRODUCTS.filter(p => p.category === selectedCategory);

  const getSampleQuery = (product: Product) => {
    if (product.category === '건강보험' || product.category === '종합보험') {
      return `${product.name}의 암 진단금은 얼마인가요?`;
    } else if (product.category === '운전자보험') {
      return `${product.name}의 사망 보장 금액은?`;
    } else {
      return `${product.name}의 보험료는 얼마인가요?`;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case '건강보험': return '🏥';
      case '운전자보험': return '🚗';
      case '종신보험': return '💎';
      case '종합보험': return '🛡️';
      default: return '📦';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-5 py-4 rounded-t-xl">
        <h3 className="text-sm font-bold text-white uppercase tracking-wide flex items-center gap-2">
          <span>🗂️</span> Product Explorer
        </h3>
        <p className="text-xs text-purple-100 mt-1">
          Browse {PRODUCTS.length} insurance products
        </p>
      </div>

      {/* Category Filter */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                selectedCategory === cat
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              {cat === 'all' ? '🌐 All' : `${getCategoryIcon(cat)} ${cat}`}
            </button>
          ))}
        </div>
      </div>

      {/* Product List */}
      <div className="p-4 max-h-96 overflow-y-auto space-y-2">
        {filteredProducts.map(product => (
          <div
            key={product.id}
            className="border border-gray-200 rounded-lg p-3 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer group"
            onClick={() => onQuerySelect(getSampleQuery(product))}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{getCategoryIcon(product.category)}</span>
                  <span className="font-semibold text-sm text-gray-800 group-hover:text-purple-600 transition-colors">
                    {product.name.length > 40 ? product.name.substring(0, 40) + '...' : product.name}
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-gray-500">{product.insurer}</span>
                  <span className="text-gray-300">•</span>
                  <span className="text-xs text-purple-600 font-semibold">
                    {product.versions.length} version{product.versions.length > 1 ? 's' : ''}
                  </span>
                </div>

                <div className="text-[11px] text-gray-600 line-clamp-1">
                  {product.description}
                </div>
              </div>

              {/* Version indicator */}
              {product.versions.length > 1 && (
                <div className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-[10px] font-bold whitespace-nowrap">
                  🔄 Multi-Ver
                </div>
              )}
            </div>

            {/* Hover action */}
            <div className="mt-2 text-[10px] text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity">
              💬 Click to ask about this product
            </div>
          </div>
        ))}
      </div>

      {/* Footer Stats */}
      <div className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 border-t border-purple-100 rounded-b-xl">
        <div className="text-[11px] text-center text-gray-600">
          Showing <span className="font-bold text-purple-600">{filteredProducts.length}</span> of{' '}
          <span className="font-bold text-purple-600">{PRODUCTS.length}</span> products
        </div>
      </div>
    </div>
  );
};
